import { ImportFileType, IUnmanagedBusiness } from '@/coreDomainsModule/entities/unmanagedBusiness';
import { convertLeadTimeToDays } from '@/coreDomainsModule/leadtime';
import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { DateService } from '@/coreDomainsModule/services/dateService';
import {
  BulkRegisterUnmanagedBusinessPostRequest,
  PostUnmanagedBusinessInfo,
  RegisterUnmanagedBusinessPostRequestItem,
  UnmanagedCustomerForecast,
} from '@/coreInfrastructureModule/axios/generated/registerBusinessManage';
import { IError } from '@/coreInfrastructureModule/interfaces/IError';
import { IRegisterBusinessRepository } from '@/coreInfrastructureModule/interfaces/IRegisterBusinessRepository';

/**
 *  未管理から取引を登録するユースケース
 */
export class RegisterBusinessFromUnmanagedBusinessUseCase {
  /**
   * コンストラクタ
   * @param repository - 未管理の取引の永続化の責務を負うクラス
   */
  constructor(private readonly repository: IRegisterBusinessRepository) {}

  /**
   * 未管理から取引を登録する
   * @param unmanagedBusiness - 未管理の取引
   * @param importFileType - 取引情報のファイルタイプ
   * @param isUpsert - 既存の取引がある場合に更新するか
   */
  public async execute(
    unmanagedBusiness: IUnmanagedBusiness,
    importFileType: ImportFileType,
    isUpsert?: boolean
  ): Promise<INetworkResult<void, Error>> {
    try {
      const request = this.convertToRequest(unmanagedBusiness, importFileType);
      const networkResult = await this.repository.postFromUnmanagedBusiness(request);

      if (networkResult.status.type === 'error') {
        return {
          status: {
            type: 'error',
            error: Error('取引情報の作成に失敗しました'),
          },
        };
      }

      return {
        status: {
          type: 'success',
          data: undefined,
        },
      };
    } catch (error) {
      console.error(error);
      return {
        status: {
          type: 'error',
          error: Error('想定外のエラーが発生しました'),
        },
      };
    }
  }

  /**
   * 未管理から取引を一括登録する
   * @param unmanagedBusinesses - 未管理の取引一覧
   * @returns 一括登録の成否
   */
  public async bulkExecute(
    unmanagedBusinesses: Array<{
      unmanagedBusiness: IUnmanagedBusiness;
      importFileType: ImportFileType;
      registerMasterInfoTypes?: string[];
    }>
  ): Promise<INetworkResult<void, IError>> {
    try {
      const registerRequests: RegisterUnmanagedBusinessPostRequestItem[] = unmanagedBusinesses.map((item) =>
        this.convertToRequest(item.unmanagedBusiness, item.importFileType)
      );
      const request: BulkRegisterUnmanagedBusinessPostRequest = { registerRequests };
      const networkResult = await this.repository.postBulkFromUnmanagedBusiness(request);
      if (networkResult.status.type === 'error') {
        return { status: { type: 'error', error: networkResult.status.error } };
      }
      if (networkResult.status.data.registeredCount !== registerRequests.length) {
        return {
          status: {
            type: 'error',
            error: {
              statusCode: 500,
              code: 'unknown',
              message: '未管理取引の一括登録件数が一致しません',
            },
          },
        };
      }

      return {
        status: {
          type: 'success',
          data: undefined,
        },
      };
    } catch (error) {
      return {
        status: {
          type: 'error',
          error: {
            statusCode: 500,
            code: 'unknown',
            message: error instanceof Error ? error.message : '想定外のエラーが発生しました',
          },
        },
      };
    }
  }

  /**
   * 未管理取引をAPIリクエスト形式へ変換する
   * @param unmanagedBusiness - 未管理の取引
   * @param importFileType - 取引情報のファイルタイプ
   */
  private convertToRequest(
    unmanagedBusiness: IUnmanagedBusiness,
    importFileType: ImportFileType
  ): RegisterUnmanagedBusinessPostRequestItem {
    if (unmanagedBusiness.leadTimeUnit === undefined || unmanagedBusiness.leadTimeUnit === '') {
      unmanagedBusiness.leadTimeUnit = 'days';
    }

    if (unmanagedBusiness.stdInventoryIndices) {
      if (
        unmanagedBusiness.stdInventoryIndices?.標準在庫指数.option.unit === undefined ||
        unmanagedBusiness.stdInventoryIndices?.標準在庫指数.option.unit === ''
      ) {
        unmanagedBusiness.stdInventoryIndices.標準在庫指数.option.unit = 'day';
      }
      if (unmanagedBusiness.stdInventoryIndices.標準在庫指数.option.unit === 'days') {
        unmanagedBusiness.stdInventoryIndices.標準在庫指数.option.unit = 'day';
      }
    }

    const businessInfo: PostUnmanagedBusinessInfo = {
      warehouse: unmanagedBusiness.warehouse,
      warehouseNumber: unmanagedBusiness.warehouseNumber,
      package: Number(unmanagedBusiness.package),
      businessNumber: unmanagedBusiness.businessNumber,
      supplierName: unmanagedBusiness.supplierName,
      supplierNumber: unmanagedBusiness.supplierNumber,
      businessName: unmanagedBusiness.businessName ?? '',
      unit: unmanagedBusiness.unit ?? '',
      leadTime: convertLeadTimeToDays(Number(unmanagedBusiness.leadTime?.toString() ?? ''), unmanagedBusiness.leadTimeUnit),
      leadTimeUnit: unmanagedBusiness.leadTimeUnit,
      moq: unmanagedBusiness.moq,
      orderPackage: unmanagedBusiness.orderPackage,
      purchaseUnitPrice: unmanagedBusiness.purchaseUnitPrice?.toString(),
      salesUnitPrice: unmanagedBusiness.salesUnitPrice?.toString(),
      stdInventoryIndices: {
        標準在庫指数: {
          min: unmanagedBusiness.stdInventoryIndices?.標準在庫指数.min ?? 0,
          max: unmanagedBusiness.stdInventoryIndices?.標準在庫指数.max ?? 0,
          option: {
            unit: unmanagedBusiness.stdInventoryIndices?.標準在庫指数.option.unit,
          },
        },
      },
      arrivals: unmanagedBusiness.arrivals ?? [],
      inventories: unmanagedBusiness.inventories ?? [],
      executionDate: DateService.formatIDateToISOString(unmanagedBusiness.executionDate).toString(),
      customerName: unmanagedBusiness.customerName ?? '',
      customerNumber: unmanagedBusiness.customerNumber ?? '',
      salesDestinationName: unmanagedBusiness.salesDestinationName ?? '',
      salesDestinationNumber: unmanagedBusiness.salesDestinationNumber ?? '',
      salesResults: unmanagedBusiness.salesResults ?? [],
      customerAnnounces:
        unmanagedBusiness.announce?.map(
          (announce): UnmanagedCustomerForecast => ({
            year: announce.year,
            month: announce.month,
            manageInitiatedYearMonth: {
              year: announce.manageInitiatedYearMonth?.year ?? 0,
              month: announce.manageInitiatedYearMonth?.month ?? 0,
            },
            amount: announce.amount,
          })
        ) ?? [],
      aggregateBusinessId: unmanagedBusiness.aggregateBusinessId,
      baseBusinessId: unmanagedBusiness.baseBusinessId ?? '',
      endUser: unmanagedBusiness.endUser,
      manufacturer: unmanagedBusiness.manufacturer,
      forecastForReceiptMemo: unmanagedBusiness.forecastForReceiptMemo,
      userCustomParameters: unmanagedBusiness.userCustomParameters,
      aggregatePlaceholderFields: unmanagedBusiness.aggregatePlaceholderFields,
      basePlaceholderFields: unmanagedBusiness.basePlaceholderFields,
      businessFreeColumnValues: (unmanagedBusiness.businessFreeColumnValues ?? []).map((item) => ({
        columnNo: item.columnNo,
        value: item.value,
        target: item.target,
      })),
    };

    const convertBusinessTypeInfo = {
      [ImportFileType.Announce]: 'customerForecast',
      [ImportFileType.SalesResult]: 'salesResult',
      [ImportFileType.Inventory]: 'inventory',
      [ImportFileType.Arrival]: 'arrival',
    };

    return {
      registerBusinessInfo: businessInfo,
      registerBusinessInfoType: convertBusinessTypeInfo[importFileType],
    };
  }
}
